import { Text, Link } from '@afterpaytouch/core'
import { Trans } from '..'
import React, { FunctionComponent, useEffect } from 'react'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import {
  setCreditCheckAgree,
  useAppDispatch,
  useCheckoutState,
  setTermsDisclosureAgree,
  openPPAModal,
  useIsAfterpayFinanceFeeEligible,
  setConfirmTermsViewed,
  useIsBillingAgreementApprovalProcess,
} from '../../state'
import { TermLink } from '../TermLink'
import styles from './style.module.css'
import { useGetTerms, useLateFeeDetails, useTermsEventProps } from './helper'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useTranslation } from '../../utils/convergence'
import { PaymentAuthorizationLink } from '../PaymentAuthorizationLink'

export const Terms: FunctionComponent = () => {
  const { t } = useTranslation()
  const { instrument, logEvent } = useMonitoringWithEnduringEventProperties()
  const { lateFee } = useLateFeeDetails()
  const { started } = useCheckoutState()
  const terms = useGetTerms()
  const termsEventProps = useTermsEventProps()
  const dispatch = useAppDispatch()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  const onInstallmentAgreementClick = (): void => {
    if (isBillingAgreementApprovalProcess) {
      return
    }

    dispatch(openPPAModal())
  }

  useEffect(() => {
    dispatch(setCreditCheckAgree(termsEventProps.creditCheckConsent))
    dispatch(setTermsDisclosureAgree(termsEventProps.termsDisclosureAgree))
  }, [termsEventProps.creditCheckConsent, termsEventProps.termsDisclosureAgree])

  useEffect(() => {
    dispatch(setConfirmTermsViewed(terms))
  }, [terms])

  if (isAFF) {
    return null
  }

  const components = {
    terms: (
      <TermLink
        key='summary-terms-of-service'
        href={t('common:termsOfServiceUrl')}
        color='Orca'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_TERMS_LINK, { outboundLink: t('common:termsOfServiceUrl') })}
      />
    ),
    privacyPolicy: (
      <TermLink
        key='summary-privacy-policy'
        href={t('common:privacyPolicyUrl')}
        color='Orca'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_PRIVACY_POLICY_LINK, { outboundLink: t('common:privacyPolicyUrl') })}
      />
    ),
    installmentAgreement: (
      <Link
        target={isBillingAgreementApprovalProcess ? '_blank' : undefined}
        href={isBillingAgreementApprovalProcess ? t('common:termsOfInstalmentAgreementUrl') : undefined}
        kind='Underline'
        color='Orca'
        testNameSpace='ppa-link-installment'
        key='summary-instalment-agreement'
        onClick={instrument(TrackingEvent.PRESSED_SUMMARY_INSTALMENT_AGREEMENT_LINK, onInstallmentAgreementClick)}
      />
    ),
    paymentAuthorization: <PaymentAuthorizationLink />,
    cardAccountAgreement: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='card-account-agreement'
        key='summary-card-account-agreement'
        target='_blank'
        href={t('summary:terms.cardAccountAgreementUrl')}
      />
    ),
  }

  const values = {
    lateFee,
    otbAmount: started?.otbAmount?.amount,
  }

  return (
    <>
      <div className={styles.wrapper}>
        <MaskedElement actionName='Terms And Conditions'>
          <Text size='XXS' color='Gray40' testNameSpace='terms-conditions'>
            <Trans i18nKey={terms} components={components} values={values} />
          </Text>
        </MaskedElement>
        <TrackOnMount eventName={TrackingEvent.VIEWED_CHECKOUT_SUMMARY_TERMS} eventProperties={{ ...termsEventProps, termsViewed: terms }} />
      </div>
    </>
  )
}
